/**
 * Standalone, Keycloak-free invite-accept app bootstrap.
 *
 * Runs in main.tsx BEFORE — and INSTEAD OF — the normal app bootstrap when the current path is
 * /invite/accept. Same pattern as verify-standalone and signup-standalone: the path check happens
 * before initKeycloak(), so `onLoad: 'login-required'` never redirects the one visitor who cannot
 * possibly sign in yet — the invited user setting their first password.
 *
 * Does NOT use TanStack Router — pre-router, mounted as a standalone React root (ADR-001's
 * deliberate short-circuit for public paths).
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { InviteAcceptApp } from './InviteAcceptApp';

export const INVITE_ACCEPT_PATH = '/invite/accept';

// Isolated from the app's global queryClient — same reasoning as SignupApp: this root never shares
// a cache with the authenticated app, which is not even mounted.
const inviteQueryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 5 * 60 * 1000 } },
});

export function runInviteAcceptApp(): void {
  const root = document.getElementById('root');
  if (!root) return;
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={inviteQueryClient}>
        <InviteAcceptApp />
      </QueryClientProvider>
    </StrictMode>,
  );
}
